import { Bell, X, Trash2, Menu, Clock, UserPlus, Copy, Check, Link, ChevronDown, ChevronUp } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

import { GlobalSearch } from './GlobalSearch';
// EmailVerificationBanner shelved per user request (Feb 2026) — email verification temporarily disabled
import { TrialStatusBanner } from './TrialStatusBanner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Header = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [reminders, setReminders] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [dealReminders, setDealReminders] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [inviteCode, setInviteCode] = useState(null);
  const [inviteCopied, setInviteCopied] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);

  useEffect(() => {
    fetchReminders();
    fetchNotifications();
    fetchDealReminders();
    const interval = setInterval(() => { fetchReminders(); fetchNotifications(); fetchDealReminders(); }, 60000);
    return () => clearInterval(interval);
  }, []);

  const fetchReminders = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/reminders`, { withCredentials: true });
      const dueReminders = data.filter(r => r.is_due);
      setReminders(dueReminders);
    } catch (error) {
      console.error('Error fetching reminders:', error);
    }
  };

  const fetchDealReminders = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/deal-reminders`, { withCredentials: true });
      setDealReminders(data);
    } catch {}
  };

  const handleDismissDealBanner = async (reminderId) => {
    try {
      await axios.put(`${API_URL}/api/deal-reminders/${reminderId}/dismiss-banner`, {}, { withCredentials: true });
      setDealReminders(prev => prev.map(r => r.id === reminderId ? { ...r, banner_dismissed: true } : r));
    } catch {}
  };

  const handleDismissDealReminder = async (reminderId) => {
    try {
      await axios.put(`${API_URL}/api/deal-reminders/${reminderId}/dismiss`, {}, { withCredentials: true });
      setDealReminders(prev => prev.filter(r => r.id !== reminderId));
      toast.success('Reminder dismissed');
    } catch {}
  };

  // Due reminders that should show as banner (not yet banner-dismissed)
  const bannerReminders = dealReminders.filter(r => r.is_due && !r.banner_dismissed);
  // Collapsed pill by default when there are > 1 banner reminders. Users can expand to see details.
  const [bannerExpanded, setBannerExpanded] = useState(false);
  // Reminders for bell dropdown: banner-dismissed but not fully dismissed
  const bellDealReminders = dealReminders.filter(r => r.is_due && r.banner_dismissed);
  // Total bell count
  const bellCount = reminders.length + notifications.length + bellDealReminders.length;

  const fetchNotifications = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/notifications`, { withCredentials: true });
      setNotifications(data.filter(n => !n.read));
    } catch {}
  };

  const handleGenerateInvite = async () => {
    if (inviteCode) return;
    setInviteLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/invite/generate`, {}, { withCredentials: true });
      setInviteCode(data.code);
    } catch { toast.error('Failed to generate invite link'); }
    setInviteLoading(false);
  };

  const fetchInviteCode = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/invite/my-link`, { withCredentials: true });
      if (data.code) setInviteCode(data.code);
    } catch {}
  };

  useEffect(() => { fetchInviteCode(); }, []);

  const inviteLink = inviteCode ? `${window.location.origin}/register?ref=${inviteCode}` : '';
  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setInviteCopied(true);
    toast.success('Invite link copied!');
    setTimeout(() => setInviteCopied(false), 2000);
  };

  const handleDismiss = async (reminderId) => {
    try {
      await axios.put(`${API_URL}/api/reminders/${reminderId}/dismiss`, {}, { withCredentials: true });
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      toast.success('Reminder dismissed');
    } catch (error) {
      toast.error('Failed to dismiss reminder');
    }
  };

  const handleDismissAll = async () => {
    try {
      await axios.post(`${API_URL}/api/reminders/dismiss-all`, {}, { withCredentials: true });
      setReminders([]);
      setShowNotifications(false);
      toast.success('All reminders dismissed');
    } catch (error) {
      toast.error('Failed to dismiss reminders');
    }
  };

  const initials = user?.name?.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';

  return (
    <div className="relative">
      <TrialStatusBanner />
      {/* EmailVerificationBanner — shelved per user request; email verification is temporarily disabled */}
      {/* <EmailVerificationBanner /> */}
      <div className="px-4 md:px-8 py-3 md:py-4 flex items-center justify-between">
      <div className="flex items-center gap-3 flex-1">
        {/* Mobile hamburger */}
        <button
          data-testid="mobile-menu-button"
          onClick={() => window.dispatchEvent(new Event('toggle-sidebar'))}
          className="md:hidden p-2 -ml-1 hover:bg-slate-50 rounded-lg transition-colors"
        >
          <Menu size={22} strokeWidth={2} className="text-slate-700" />
        </button>

        {/* Branding - hide on mobile to save space */}
        <div className="hidden sm:block">
          {user?.company_logo ? (
            <img src={user.company_logo} alt="Company" className="max-h-8 max-w-[160px] object-contain" data-testid="header-company-logo" />
          ) : user?.company_branding ? (
            <span className="text-sm font-semibold text-slate-500" data-testid="header-company-branding">{user.company_branding}</span>
          ) : null}
        </div>

        {/* Global Deal Search */}
        <div className="hidden md:block flex-1 max-w-sm ml-4">
          <GlobalSearch />
        </div>
      </div>
      
      <div className="flex items-center gap-2 md:gap-4">
        {/* Invite Button */}
        <Popover onOpenChange={(open) => { if (open) handleGenerateInvite(); }}>
          <PopoverTrigger asChild>
            <button
              data-testid="invite-button"
              className="p-2 hover:bg-slate-50 rounded-lg transition-colors relative border border-transparent hover:border-slate-200"
              title="Invite someone"
            >
              <UserPlus size={20} strokeWidth={2} className="text-slate-500" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-0 rounded-xl shadow-lg" align="end">
            <div className="p-4 border-b border-slate-100">
              <h3 className="font-bold text-slate-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>Invite to Deal Desk</h3>
              <p className="text-xs text-slate-500 mt-0.5">Share this link to invite someone to create an account</p>
            </div>
            <div className="p-4">
              {inviteLoading ? (
                <div className="flex items-center justify-center py-3">
                  <div className="w-5 h-5 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
                </div>
              ) : inviteCode ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg p-2.5">
                    <Link size={14} className="text-slate-400 flex-shrink-0" />
                    <span className="text-xs text-slate-600 truncate flex-1 font-mono">{inviteLink}</span>
                  </div>
                  <button
                    data-testid="copy-invite-link-btn"
                    onClick={copyInviteLink}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                      inviteCopied
                        ? 'bg-emerald-600 text-white'
                        : 'bg-indigo-600 hover:bg-indigo-700 text-white'
                    }`}
                  >
                    {inviteCopied ? <><Check size={15} /> Copied!</> : <><Copy size={15} /> Copy Invite Link</>}
                  </button>
                </div>
              ) : (
                <p className="text-sm text-slate-500 text-center py-2">Unable to generate link</p>
              )}
            </div>
          </PopoverContent>
        </Popover>

        <div className="relative">
          <button
            data-testid="notification-bell-button"
            onClick={() => setShowNotifications(!showNotifications)}
            className="p-2 hover:bg-slate-50 rounded-lg transition-colors relative border border-transparent hover:border-slate-200"
          >
            <Bell size={20} strokeWidth={2} className="text-slate-500" />
            {bellCount > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-[10px] font-bold text-white">{bellCount}</span>
              </span>
            )}
          </button>
          
          {showNotifications && (
            <div className="absolute right-0 mt-2 w-80 md:w-96 bg-white border border-slate-200 rounded-xl shadow-lg z-50">
              <div className="p-4 border-b border-slate-200 flex items-center justify-between">
                <h3 className="font-bold text-slate-900 text-sm md:text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>Notifications</h3>
                {(reminders.length + notifications.length) > 0 && (
                  <button
                    data-testid="dismiss-all-reminders-btn"
                    onClick={async () => {
                      await handleDismissAll();
                      try {
                        await axios.post(`${API_URL}/api/notifications/read-all`, {}, { withCredentials: true });
                        setNotifications([]);
                      } catch {}
                    }}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1"
                  >
                    <Trash2 size={12} />
                    Clear All
                  </button>
                )}
              </div>
              <div className="max-h-96 overflow-y-auto">
                {/* Manager comment notifications */}
                {notifications.map((notif) => (
                  <div key={notif.id} data-testid={`notification-${notif.id}`} className="p-4 border-b border-slate-100 hover:bg-indigo-50/50 group cursor-pointer"
                    onClick={async () => {
                      try {
                        await axios.put(`${API_URL}/api/notifications/${notif.id}/read`, {}, { withCredentials: true });
                        setNotifications(prev => prev.filter(n => n.id !== notif.id));
                      } catch {}
                      if (notif.account_id) navigate(`/account/${notif.account_id}`);
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <span className="text-xs font-bold text-violet-600">{(notif.from_name || 'M')[0].toUpperCase()}</span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{notif.message}</p>
                        <p className="text-xs text-slate-400 mt-0.5">
                          {notif.created_at ? (() => {
                            const d = new Date(notif.created_at);
                            const now = new Date();
                            const diff = Math.floor((now - d) / 1000 / 60);
                            if (diff < 60) return `${diff}m ago`;
                            if (diff < 1440) return `${Math.floor(diff/60)}h ago`;
                            return `${Math.floor(diff/1440)}d ago`;
                          })() : ''}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Deal reminders (banner-dismissed, living in bell) */}
                {bellDealReminders.map((r) => (
                  <div key={r.id} data-testid={`bell-deal-reminder-${r.id}`}
                    className="p-4 border-b border-slate-100 hover:bg-amber-50/50 group cursor-pointer"
                    onClick={() => navigate(`/account/${r.account_id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Clock size={14} className="text-amber-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900">{r.note || 'Reminder'}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{r.account_name}</p>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDismissDealReminder(r.id); }}
                        className="text-slate-400 hover:text-red-500 p-1"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Renewal reminders */}
                {reminders.length === 0 && notifications.length === 0 && bellDealReminders.length === 0 ? (
                  <div className="p-6 text-center text-slate-500">
                    <Bell size={24} className="mx-auto mb-2 text-slate-300" />
                    <p className="text-sm">No pending notifications</p>
                  </div>
                ) : (
                  reminders.map((reminder) => (
                    <div key={reminder.id} data-testid={`reminder-item-${reminder.id}`} className="p-4 border-b border-slate-100 hover:bg-slate-50 group">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="font-semibold text-slate-900 text-sm">{reminder.account_name}</p>
                          <p className="text-xs text-slate-500 mt-1">Competitor: {reminder.competitor_platform}</p>
                          <p className="text-xs text-slate-500">Renewal: {new Date(reminder.renewal_date).toLocaleDateString()}</p>
                        </div>
                        <button
                          data-testid={`dismiss-reminder-${reminder.id}`}
                          onClick={() => handleDismiss(reminder.id)}
                          className="p-1 text-slate-300 hover:text-slate-600"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex items-center gap-2 md:gap-3">
          <Avatar className="w-8 h-8 md:w-10 md:h-10">
            <AvatarImage src={user?.avatar_url} />
            <AvatarFallback className="bg-indigo-600 text-white text-xs md:text-sm">{initials}</AvatarFallback>
          </Avatar>
          <div className="hidden sm:block">
            <p className="text-sm font-medium text-slate-900">{user?.name}</p>
            <p className="text-xs text-slate-500">{user?.email}</p>
            <span className={`inline-block text-[10px] font-bold uppercase tracking-wider mt-0.5 px-1.5 py-0.5 rounded-full ${
              user?.role === 'manager' ? 'bg-violet-100 text-violet-700' : 'bg-indigo-100 text-indigo-700'
            }`}>
              {user?.role === 'manager' ? 'Manager' : 'Account Executive'}
            </span>
          </div>
        </div>
      </div>

      {/* Floating Reminder Pill (collapsible stack) */}
      {bannerReminders.length > 0 && (
        <div className="fixed top-4 right-4 z-50 max-w-sm" data-testid="reminder-banner">
          {!bannerExpanded && bannerReminders.length > 1 ? (
            // Collapsed single pill — one row showing count
            <button
              data-testid="reminder-pill"
              onClick={() => setBannerExpanded(true)}
              className="group bg-white rounded-full border border-amber-200 shadow-lg pl-2 pr-4 py-2 flex items-center gap-2 hover:border-amber-300 hover:shadow-xl transition-all"
              title="Show all due reminders"
            >
              <span className="relative flex items-center justify-center w-7 h-7 rounded-full bg-amber-100">
                <Bell size={14} className="text-amber-600" />
                <span className="absolute inset-0 rounded-full bg-amber-400 opacity-40 animate-ping" />
              </span>
              <span className="text-sm font-bold text-slate-900">{bannerReminders.length}</span>
              <span className="text-sm font-semibold text-slate-600 group-hover:text-slate-800">reminder{bannerReminders.length === 1 ? '' : 's'} due</span>
              <ChevronDown size={14} className="text-slate-400 group-hover:text-slate-600 ml-0.5" />
            </button>
          ) : (
            // Expanded panel — original stacked cards, now scrollable + has a Collapse control
            <div className="flex flex-col gap-2">
              {bannerReminders.length > 1 && (
                <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5 shadow-sm">
                  <div className="flex items-center gap-2 text-xs font-bold text-amber-800">
                    <Bell size={12} />
                    {bannerReminders.length} reminders due
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      data-testid="dismiss-all-reminders"
                      onClick={async () => {
                        // Fire dismiss-banner for each; UI will optimistically update via handleDismissDealBanner
                        for (const r of bannerReminders) {
                          // eslint-disable-next-line no-await-in-loop
                          await handleDismissDealBanner(r.id);
                        }
                      }}
                      className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 px-2 py-0.5 rounded hover:bg-amber-100"
                      title="Move all to the bell"
                    >
                      Dismiss all
                    </button>
                    <button
                      data-testid="collapse-reminder-pill"
                      onClick={() => setBannerExpanded(false)}
                      className="p-1 rounded text-amber-600 hover:text-amber-800 hover:bg-amber-100"
                      title="Collapse"
                    >
                      <ChevronUp size={14} />
                    </button>
                  </div>
                </div>
              )}
              <div className="flex flex-col gap-2 max-h-[70vh] overflow-y-auto pr-1">
                {bannerReminders.map((r, idx) => (
                  <div
                    key={r.id}
                    data-testid={`reminder-banner-${r.id}`}
                    className="bg-white rounded-xl border border-slate-200 shadow-lg p-4 flex items-start gap-3 animate-in slide-in-from-right"
                    style={{ animationDelay: `${idx * 60}ms`, animationDuration: '300ms', animationFillMode: 'both' }}
                  >
                    <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Bell size={16} className="text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/account/${r.account_id}`)}>
                      <p className="text-sm font-bold text-slate-900 leading-tight">{r.note || 'Reminder due'}</p>
                      <p className="text-xs text-slate-500 mt-0.5">{r.account_name}</p>
                    </div>
                    <button
                      data-testid={`dismiss-banner-${r.id}`}
                      onClick={() => handleDismissDealBanner(r.id)}
                      className="flex-shrink-0 p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors -mt-1 -mr-1"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
};
